import React from "react";
import type { FileSort } from "../../services/filesService";

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  sort: FileSort;
  onSortChange: (value: FileSort) => void;
  onNewMindmap: () => void;
  onBack: () => void;
}

const SORT_OPTIONS: Array<{ value: FileSort; label: string }> = [
  { value: "updated", label: "Last updated" },
  { value: "created", label: "Created" },
  { value: "title", label: "Title A-Z" },
];

const SORT_SELECT_ID = "my-files-sort";

const CONTROL_HEIGHT = "2.25rem";
const CONTROL_FONT_SIZE = "0.9rem";
const CONTROL_RADIUS = "0.375rem";
const CONTROL_BORDER = "1.5px solid var(--mf-control-border)";
const SORT_SELECT_WIDTH = "9rem";

const controlBaseStyle: React.CSSProperties = {
  height: CONTROL_HEIGHT,
  boxSizing: "border-box",
  borderRadius: CONTROL_RADIUS,
  fontSize: CONTROL_FONT_SIZE,
  lineHeight: 1.2,
};

export const FilesTopBar: React.FC<Props> = ({
  search,
  onSearchChange,
  sort,
  onSortChange,
  onNewMindmap,
  onBack,
}) => {
  return (
    // Sticky against the page's own scroll container (the `overflowY: auto`
    // wrapper in MyFilesPage) so the controls stay reachable while the grid
    // scrolls; the opaque background + border keep cards from bleeding through.
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        flexWrap: "wrap",
        padding: "1.25rem 2rem",
        background: "var(--mf-bg)",
        borderBottom: "1px solid var(--mf-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        {/* Just "Back": it returns to wherever the user came from (the file they
            were editing, or the canvas), not always the canvas. */}
        <button
          type="button"
          aria-label="Back"
          title="Back"
          onClick={onBack}
          style={{
            ...controlBaseStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: CONTROL_HEIGHT,
            border: CONTROL_BORDER,
            background: "transparent",
            padding: 0,
            color: "var(--mf-text)",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <span aria-hidden="true">←</span>
        </button>
        <h1
          style={{
            fontSize: "1.35rem",
            margin: 0,
            fontWeight: 700,
            color: "var(--mf-text)",
          }}
        >
          My Files
        </h1>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <input
          type="search"
          className="mf-search-input"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by title"
          aria-label="Search by title"
          style={{
            ...controlBaseStyle,
            border: CONTROL_BORDER,
            padding: "0 0.75rem",
            minWidth: "12rem",
            background: "var(--mf-control-bg)",
            color: "var(--mf-text)",
          }}
        />
        <label
          htmlFor={SORT_SELECT_ID}
          style={{
            fontSize: CONTROL_FONT_SIZE,
            color: "var(--mf-text-muted)",
          }}
        >
          Sort
        </label>
        {/*
          A native <select> rather than a custom listbox: the browser's own
          popup gets keyboard, focus and dismissal right for free (the custom
          one committed a sort — and a refetch — on every arrow key).
        */}
        <div style={{ position: "relative", width: SORT_SELECT_WIDTH }}>
          <select
            id={SORT_SELECT_ID}
            className="mf-sort-select"
            value={sort}
            onChange={(e) => onSortChange(e.target.value as FileSort)}
            aria-label="Sort files"
            style={{
              ...controlBaseStyle,
              appearance: "none",
              width: "100%",
              border: CONTROL_BORDER,
              padding: "0 1.75rem 0 0.65rem",
              background: "var(--mf-control-bg)",
              color: "var(--mf-text)",
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              right: "0.7rem",
              top: "calc(50% - 0.35rem)",
              width: "0.45rem",
              height: "0.45rem",
              borderRight: "1.5px solid var(--mf-text-muted)",
              borderBottom: "1.5px solid var(--mf-text-muted)",
              transform: "rotate(45deg)",
              pointerEvents: "none",
            }}
          />
        </div>
        <button
          type="button"
          onClick={onNewMindmap}
          style={{
            ...controlBaseStyle,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--mf-accent)",
            color: "var(--mf-accent-text)",
            padding: "0 1rem",
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          New mindmap
        </button>
      </div>
    </div>
  );
};

export default FilesTopBar;
